import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaPaintBrush, FaHammer, FaBuilding, FaHardHat, FaToolbox, FaRulerCombined,
  FaMapMarkerAlt, FaDollarSign, FaClipboardList, FaCheckCircle, FaStar, FaUsers,
  FaChartLine, FaShieldAlt, FaClock, FaAward
} from 'react-icons/fa';
import PricingPlans from './PricingPlans';
import MapView from './MapView';
import EstimateForm from './EstimateForm';

const HomePage = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  const images = [
    { src: '/images/1.jpeg', text: 'Construye tu visión', subtitle: 'Proyectos que transforman espacios' },
    { src: '/images/2.jpeg', text: 'Innovación y diseño', subtitle: 'Tecnología al servicio de tus ideas' },
    { src: '/images/3.jpeg', text: 'Calidad garantizada', subtitle: 'Profesionales certificados' },
    { src: '/images/4.jpeg', text: 'Experiencia profesional', subtitle: 'Más de 10 años de trayectoria' },
    { src: '/images/5.jpeg', text: 'Resultados excepcionales', subtitle: 'Superando expectativas' },
    { src: '/images/6.jpeg', text: 'Soluciones personalizadas', subtitle: 'Adaptadas a tus necesidades' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [images.length]);

  // Intersection Observer para animaciones
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const services = [
    { 
      Icon: FaPaintBrush, 
      title: 'Pintura', 
      description: 'Transforme sus espacios con colores vibrantes y acabados profesionales',
      color: 'from-blue-500 to-blue-600'
    },
    { 
      Icon: FaHammer, 
      title: 'Renovación', 
      description: 'Renueve y actualice su hogar o negocio con las últimas tendencias',
      color: 'from-green-500 to-green-600'
    },
    { 
      Icon: FaBuilding, 
      title: 'Arquitectura', 
      description: 'Diseños innovadores y funcionales para cada proyecto',
      color: 'from-purple-500 to-purple-600'
    },
    { 
      Icon: FaHardHat, 
      title: 'Ingeniería', 
      description: 'Soluciones técnicas avanzadas y estructuras seguras',
      color: 'from-orange-500 to-orange-600'
    },
    { 
      Icon: FaToolbox, 
      title: 'Carpintería', 
      description: 'Trabajos en madera de alta calidad con diseño exclusivo',
      color: 'from-yellow-500 to-yellow-600'
    },
    { 
      Icon: FaRulerCombined, 
      title: 'Diseño de Interiores', 
      description: 'Cree espacios funcionales, estéticos y acogedores',
      color: 'from-pink-500 to-pink-600'
    },
  ];

  const stats = [
    { Icon: FaUsers, value: '10,000+', label: 'Clientes Satisfechos' },
    { Icon: FaBuilding, value: '5,000+', label: 'Proyectos Completados' },
    { Icon: FaStar, value: '4.9/5', label: 'Calificación Promedio' },
    { Icon: FaAward, value: '15+', label: 'Años de Experiencia' },
  ];

  const features = [
    {
      Icon: FaCheckCircle,
      title: 'Verificación de Profesionales',
      description: 'Todos nuestros proveedores están verificados y certificados'
    },
    {
      Icon: FaDollarSign,
      title: 'Precios Transparentes',
      description: 'Cotizaciones claras sin costos ocultos'
    },
    {
      Icon: FaClock,
      title: 'Respuesta Rápida',
      description: 'Obtén respuestas en menos de 24 horas'
    },
    {
      Icon: FaShieldAlt,
      title: 'Garantía de Calidad',
      description: 'Protección y garantía en todos los proyectos'
    }
  ];

  const testimonials = [
    {
      name: 'María González',
      role: 'Propietaria de Casa',
      comment: 'Excelente servicio, encontré al contratista perfecto para mi renovación.',
      rating: 5
    },
    {
      name: 'Carlos Rodríguez',
      role: 'Empresario',
      comment: 'Profesionales de primera. Mi oficina quedó espectacular.',
      rating: 5
    },
    {
      name: 'Ana Martínez',
      role: 'Arquitecta',
      comment: 'La mejor plataforma para conectar con clientes. Muy recomendada.',
      rating: 5
    }
  ];

  return (
    <div className="flex flex-col overflow-hidden">
      {/* Hero Section con Diseño Moderno */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background con efecto parallax */}
        <div 
          className="absolute inset-0 bg-cover bg-center transition-all duration-1000 transform scale-105"
          style={{ 
            backgroundImage: `url(${images[currentSlide].src})`,
            filter: 'brightness(0.4)'
          }}
        />
        
        {/* Overlay con gradiente */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        
        {/* Elementos decorativos */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        {/* Contenido Principal */}
        <div className="relative z-10 container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8"
            >
              <FaAward className="text-yellow-400" />
              <span className="text-white text-sm font-medium">Plataforma #1 en Construcción</span>
            </motion.div>

            {/* Título Principal */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-5xl md:text-7xl font-display font-bold mb-6 leading-tight"
            >
              <span className="block text-white mb-2">{images[currentSlide].text}</span>
              <span className="block bg-gradient-to-r from-primary-400 via-accent-400 to-secondary-400 bg-clip-text text-transparent">
                {images[currentSlide].subtitle}
              </span>
            </motion.h1>

            {/* Subtítulo */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-xl md:text-2xl text-gray-200 mb-12 max-w-2xl mx-auto font-light"
            >
              Conectando sueños con los mejores profesionales de la construcción
            </motion.p>

            {/* Botones CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <button
                onClick={() => navigate('/signup')}
                className="group relative px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-full font-semibold text-lg shadow-2xl hover:shadow-glow-lg transform hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <FaHardHat />
                  Soy Proveedor
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-primary-600 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
              </button>
              
              <button
                onClick={() => navigate('/signup')}
                className="group px-8 py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white rounded-full font-semibold text-lg hover:bg-white hover:text-primary-600 transform hover:-translate-y-1 transition-all duration-300"
              >
                <span className="flex items-center gap-2">
                  <FaBuilding />
                  Soy Cliente
                </span>
              </button>
            </motion.div>

            {/* Indicadores de slides */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="flex justify-center gap-2 mt-12"
            >
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? 'w-12 bg-white' 
                      : 'w-2 bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <div className="flex flex-col items-center gap-2 text-white/60 animate-bounce">
            <span className="text-sm">Descubre más</span>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </motion.div>
      </section>

      {/* Estadísticas */}
      <section className="py-20 bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center text-white"
              >
                <stat.Icon className="w-12 h-12 mx-auto mb-4 opacity-80" />
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-sm md:text-base opacity-90">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Características Principales */}
      <section className="py-20 bg-white" id="features" data-animate>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">¿Por qué elegirnos?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ofrecemos la mejor experiencia para conectar clientes y proveedores
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card card-hover p-8 text-center group"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  <feature.Icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Servicios con diseño moderno */}
      <section id="services" className="py-20 bg-gray-50" data-animate>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Servicios Especializados</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ofrecemos una amplia gama de servicios profesionales para todos tus proyectos
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card card-hover group cursor-pointer"
              >
                <div className="relative overflow-hidden">
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                  <div className="p-8">
                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                      <service.Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3 group-hover:text-primary-600 transition-colors">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                    <div className="mt-6 flex items-center text-primary-600 font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                      <span>Ver más</span>
                      <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mapa de Servicios */}
      <section id="map" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Encuentra o Ofrece Servicios</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Conecta con profesionales cerca de ti o publica tus servicios
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="rounded-3xl overflow-hidden shadow-2xl"
          >
            <MapView />
          </motion.div>
        </div>
      </section>

      {/* Testimonios */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Lo que dicen nuestros clientes</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Miles de clientes satisfechos confían en nosotros
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card p-8"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="w-5 h-5 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.comment}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-bold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Planes de Precios */}
      <section id="pricing" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Planes Flexibles para Ti</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Elige el plan que mejor se adapte a tus necesidades
            </p>
          </motion.div>
          <PricingPlans />
        </div>
      </section>

      {/* Formulario de Presupuesto */}
      <section id="estimate" className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Solicita tu Presupuesto</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Obtén una cotización personalizada para tu proyecto
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <EstimateForm />
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600" />
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative container mx-auto px-4 text-center text-white"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            ¿Listo para Transformar tu Espacio?
          </h2>
          <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto opacity-90">
            Únete a nuestra comunidad de profesionales y clientes satisfechos. Comienza tu proyecto hoy y haz realidad tus ideas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="group px-10 py-5 bg-white text-primary-600 rounded-full font-bold text-lg shadow-2xl hover:shadow-glow-lg transform hover:-translate-y-1 transition-all duration-300"
            >
              <span className="flex items-center justify-center gap-2">
                Comienza Ahora
                <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="px-10 py-5 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white rounded-full font-bold text-lg hover:bg-white/20 transform hover:-translate-y-1 transition-all duration-300"
            >
              Contáctanos
            </button>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default HomePage;